import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../utils/cn';
import Icon from '../../../../../components/AppIcon';
import Button from '../../../../../components/ui/Button';
import Select from '../../../../../components/ui/Select';
import { Spinner } from '../../../../../components/ui/Spinner';
import type { BrandingSettings } from '../types';
import { LOGO_POSITION_OPTIONS, LOGO_SIZE_OPTIONS } from '../types';
import type { UseLogoUploadReturn } from '../hooks/useLogoUpload';

interface LogoUploadSectionProps {
  brandingSettings: BrandingSettings;
  setBrandingSettings: React.Dispatch<React.SetStateAction<BrandingSettings>>;
  logoUpload: UseLogoUploadReturn;
}

const LogoUploadSection: React.FC<LogoUploadSectionProps> = ({
  brandingSettings,
  setBrandingSettings,
  logoUpload,
}) => {
  const { t } = useTranslation();
  const {
    dragActive,
    uploadingLogo,
    uploadProgress,
    fileInputRef,
    handleDrop,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleFileInput,
    handleRemoveLogo,
    getMaxLogoSizeMB,
  } = logoUpload;

  const { logoSettings } = brandingSettings;

  return (
    <div className="mb-6" data-testid="logo-upload">
      <h4 className="text-sm font-medium text-foreground mb-4">
        {t('Company Logo')}
      </h4>

      {!logoSettings.hasLogo ? (
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-muted-foreground/50'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          {uploadingLogo ? (
            <div className="mx-auto mb-4 flex justify-center">
              <Spinner size="lg" />
            </div>
          ) : (
            <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
          )}
          <p className="text-sm text-muted-foreground mb-4">
            {t('Drag and drop your logo here, or click to browse')}
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
            id="logo-upload"
            ref={fileInputRef}
            disabled={uploadingLogo}
          />
          <div className="flex items-center justify-center flex-col">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              {uploadingLogo
                ? t('Uploading') + ` ${uploadProgress}%`
                : t('Choose File')}
            </Button>

            {uploadingLogo && (
              <div className="w-full mt-4 max-w-xs">
                {/* TODO(batch5): Replace with shared LinearProgress component */}
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground text-center mt-1">
                  {uploadProgress}%
                </div>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t('Supported formats: JPG, PNG, GIF, SVG (Max {{maxMB}}MB)', {
              maxMB: getMaxLogoSizeMB(),
            })}
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h5 className="text-sm font-medium text-foreground">
              {t('Current Logo')}
            </h5>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRemoveLogo}
              disabled={uploadingLogo}
            >
              {t('Remove')}
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <img
              src={logoSettings.logoUrl || ''}
              alt={t('Company Logo Preview')}
              className="w-20 h-20 object-contain border border-border rounded"
            />
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                  label={t('Position')}
                  options={LOGO_POSITION_OPTIONS}
                  value={logoSettings.logoPosition}
                  onChange={(val: string) =>
                    setBrandingSettings((prev) => ({
                      ...prev,
                      logoSettings: { ...prev.logoSettings, logoPosition: val },
                    }))
                  }
                />
                <Select
                  label={t('Size')}
                  options={LOGO_SIZE_OPTIONS}
                  value={logoSettings.logoSize}
                  onChange={(val: string) =>
                    setBrandingSettings((prev) => ({
                      ...prev,
                      logoSettings: { ...prev.logoSettings, logoSize: val },
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogoUploadSection;
